import { numbersIn } from "../job/details.js";
/**
 * Free text from the other side, translated on the reader's phone (multilanguage plan §3.4). The original is kept and
 * always shown; a translation is only a view of it. Core holds the rules; the engine (Chrome's built-in translator,
 * ML Kit on Android) sits behind `TranslateEngine` in the app, and nothing here ever calls a cloud service.
 */
/** What an engine can do for one language pair on this device. */
export const TranslateState = {
    available: "available",
    /** A language pack exists but must be fetched first; engines want a user's tap for that. */
    downloadable: "downloadable",
    downloading: "downloading",
    unavailable: "unavailable",
};
export const TranslationKind = { translated: "translated", unsafe: "unsafe", unavailable: "unavailable" };
/**
 * True when every number in the original is still in the translation, in any script. A model may never change an
 * amount, a time or a count; extra numbers ("third" becoming "3rd") are allowed.
 */
export function numbersKept(original, translated) {
    const left = numbersIn(translated);
    for (const n of numbersIn(original)) {
        const at = left.indexOf(n);
        if (at === -1)
            return false;
        left.splice(at, 1);
    }
    return true;
}
/** Enough for a screen of jobs and notes; the oldest entry goes first. */
const MAX_CACHED = 500;
const SEPARATOR = "\u0000";
/** Translates through one engine, once per text and pair, with the number check applied to every result. */
export class Translations {
    engine;
    cache = new Map();
    constructor(engine) {
        this.engine = engine;
    }
    translate(text, from, to) {
        const key = [from, to, text].join(SEPARATOR);
        const hit = this.cache.get(key);
        if (hit)
            return hit;
        const next = this.run(text, from, to);
        this.cache.set(key, next);
        if (this.cache.size > MAX_CACHED)
            this.cache.delete(this.cache.keys().next().value);
        // A pair that was not ready may be ready later (after a download): do not keep "unavailable" answers.
        void next.then((r) => {
            if (r.kind === TranslationKind.unavailable)
                this.cache.delete(key);
        });
        return next;
    }
    async run(text, from, to) {
        try {
            const state = await this.engine.state(from, to);
            if (state !== TranslateState.available)
                return { kind: TranslationKind.unavailable, state };
            const out = (await this.engine.translate(text, from, to))?.trim();
            if (!out)
                return { kind: TranslationKind.unavailable, state: TranslateState.unavailable };
            return numbersKept(text, out)
                ? { kind: TranslationKind.translated, text: out }
                : { kind: TranslationKind.unsafe };
        }
        catch {
            return { kind: TranslationKind.unavailable, state: TranslateState.unavailable };
        }
    }
}
/** The engine on a device that has none: every pair is unavailable and the original is shown alone. */
export const NO_TRANSLATOR = {
    name: "none",
    state: async () => TranslateState.unavailable,
    prepare: async () => TranslateState.unavailable,
    translate: async () => undefined,
};
//# sourceMappingURL=translate.js.map